import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DatePicker from '../components/Common/DatePicker';
import { attendanceService, employeeService, authService, attendanceStructureService } from '../services/api';
import { useAttendanceCodeConfig } from '../context/attendanceCodeConfigContext';
import { AlertTriangle } from 'lucide-react';

const statusStyles = {
  'PR': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'WO': 'bg-gray-100 text-gray-800 border-gray-200',
  'PH': 'bg-purple-100 text-purple-800 border-purple-200',
  'HO': 'bg-purple-100 text-purple-800 border-purple-200',
  'CL': 'bg-amber-100 text-amber-800 border-amber-200',
  'EL': 'bg-blue-100 text-blue-800 border-blue-200',
  'LOP': 'bg-red-100 text-red-800 border-red-200',
  'KL': 'bg-amber-100 text-amber-800 border-amber-200',
  'ML': 'bg-sky-100 text-sky-800 border-sky-200',
  'BL': 'bg-slate-100 text-slate-800 border-slate-200',
  'CO': 'bg-orange-100 text-orange-800 border-orange-200',
  'CW': 'bg-teal-100 text-teal-800 border-teal-200',
};

// Default config codes (fallback when API not available)
const DEFAULT_LEGEND_CONFIGS = [
  { meaning: 'Present', originalCode: 'PR', customCode: 'PR' },
  { meaning: 'Loss of Pay', originalCode: 'LOP', customCode: 'LOP' },
  { meaning: 'Weekly Off', originalCode: 'WO', customCode: 'WO' },
  { meaning: 'Public Holiday', originalCode: 'PH', customCode: 'PH' },
];

// Get status style with fallback for custom codes
const getStatusStyle = (status, attendanceMapping) => {
  if (!status || status === '-') return '';
  const normalized = String(status).toUpperCase();
  // Check if it's a custom code that needs to be mapped back
  const originalCode = attendanceMapping?.reverseMapping?.[normalized] || normalized;
  return statusStyles[originalCode] || 'bg-gray-100 text-gray-800 border-gray-200';
};

// Get display status (custom code if available, otherwise original)
// Handles combined statuses like "PR/LW" by mapping each part
const getDisplayStatus = (status, attendanceMapping) => {
  if (!status || status === '-') return null;
  const normalized = String(status).toUpperCase();
  
  // Handle combined statuses (e.g., "PR/LW")
  if (normalized.includes('/')) {
    const parts = normalized.split('/');
    const mappedParts = parts.map(part => {
      const trimmed = part.trim();
      if (attendanceMapping?.reverseMapping?.[trimmed]) {
        return trimmed; // Already a custom code
      }
      return attendanceMapping?.mapping?.[trimmed] || trimmed;
    });
    return mappedParts.join('/');
  }
  
  // Single status
  if (attendanceMapping?.reverseMapping?.[normalized]) {
    // This is a custom code, use it as-is
    return normalized;
  }
  // Map original code to custom code if available
  return attendanceMapping?.mapping?.[normalized] || normalized;
};

export default function EmployeeAttendance() {
  const { mapping, reverseMapping } = useAttendanceCodeConfig();
  const attendanceMapping = useMemo(() => ({ mapping, reverseMapping }), [mapping, reverseMapping]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceLocks, setAttendanceLocks] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLocked, setIsLocked] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [editDay, setEditDay] = useState(null);
  const [savingDay, setSavingDay] = useState(null);
  const [legendConfigs, setLegendConfigs] = useState([]);
  const [legendLeaveTypes, setLegendLeaveTypes] = useState([]);
  const [weeklyOffPolicies, setWeeklyOffPolicies] = useState([]);
  const [employeeWeeklyOffMappings, setEmployeeWeeklyOffMappings] = useState({});
  const [holidaysData, setHolidaysData] = useState([]);
  const [holidayPolicies, setHolidayPolicies] = useState([]);
  const [employeeHolidayMappings, setEmployeeHolidayMappings] = useState({});

  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  // Fetch admin-configured default codes (Configuration) and leave types (Leave Management) for legend
  useEffect(() => {
    const loadLegendData = async () => {
      try {
        const [configs, ltResp] = await Promise.all([
          attendanceService.getConfigurations().catch(() => []),
          attendanceStructureService.getLeaveTypes().catch(() => ({ success: false, data: [] })),
        ]);
        setLegendConfigs(Array.isArray(configs) && configs.length > 0 ? configs : DEFAULT_LEGEND_CONFIGS);
        setLegendLeaveTypes(Array.isArray(ltResp?.data) ? ltResp.data : []);
      } catch (e) {
        console.warn('Failed to load legend data:', e);
        setLegendConfigs(DEFAULT_LEGEND_CONFIGS);
        setLegendLeaveTypes([]);
      }
    };
    loadLegendData();
  }, []);

  // Fetch employee profile to get joining date (use only employeeId – works for any logged-in employee)
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        const currentUserResponse = await authService.getCurrentUser();
        if (currentUserResponse && currentUserResponse.data) {
          const userData = currentUserResponse.data;
          const employeeId = userData.employeeId;
          if (employeeId) {
            const employeeResponse = await employeeService.getEmployee(employeeId);
            if (employeeResponse && employeeResponse.data) {
              setEmployee(employeeResponse.data);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch employee data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeData();
  }, []);

  // Fetch attendance locks
  useEffect(() => {
    const loadLocks = async () => {
      try {
        const response = await attendanceStructureService.getAttendanceLocks();
        if (response.success && Array.isArray(response.data)) {
          setAttendanceLocks(response.data || []);
          const currentLock = response.data.find(
            (lock) => lock.month === selectedMonth + 1 && lock.year === selectedYear
          );
          setIsLocked(!!currentLock);
        } else {
          setAttendanceLocks([]);
          setIsLocked(false);
        }
      } catch (err) {
        console.error('Failed to load attendance locks:', err);
        setAttendanceLocks([]);
        setIsLocked(false);
      }
    };
    loadLocks();
  }, [selectedMonth, selectedYear]);

  // Fetch weekly off, holidays and policy mappings (for default PR/WO/PH when locked and backend is empty)
  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const [woResp, woMapResp, holidaysResp, hpResp, hpMapResp] = await Promise.all([
          attendanceStructureService.getWeeklyOffPolicies().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getEmployeeWeeklyOffPolicies().catch(() => ({ success: false, data: {} })),
          attendanceStructureService.getHolidays().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getHolidayPolicies().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getEmployeeHolidayPolicies().catch(() => ({ success: false, data: {} })),
        ]);
        setWeeklyOffPolicies(woResp?.data || []);
        setEmployeeWeeklyOffMappings(woMapResp?.data || {});
        setHolidaysData(holidaysResp?.data || []);
        setHolidayPolicies(hpResp?.data || []);
        setEmployeeHolidayMappings(hpMapResp?.data || {});
      } catch (e) {
        console.warn('Failed to load attendance policies:', e);
      }
    };
    loadPolicies();
  }, []);

  // Fetch attendance data for selected month (always fetch so employee sees data like admin view)
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!employee) {
        setAttendanceData({});
        return;
      }

      try {
        setLoadingAttendance(true);
        const response = await attendanceService.getMyAttendanceHistory(
          selectedMonth + 1, // API expects 1-12
          selectedYear
        );

        if (response.success && response.data && Array.isArray(response.data)) {
          // Transform attendance array to map by day (parse YYYY-MM-DD safely to avoid timezone shifts)
          const attendanceMap = {};
          response.data.forEach((record) => {
            const raw = record.date;
            if (!raw || typeof raw !== 'string') return;
            const parts = raw.trim().split(/[-/]/);
            if (parts.length < 3) return;
            const day = parseInt(parts[2], 10);
            if (Number.isNaN(day) || day < 1 || day > 31) return;
            attendanceMap[day] = record.status || '-';
          });
          setAttendanceData(attendanceMap);
        } else {
          setAttendanceData({});
        }
      } catch (error) {
        console.error('Failed to fetch attendance:', error);
        setAttendanceData({});
      } finally {
        setLoadingAttendance(false);
      }
    };

    fetchAttendance();
  }, [employee, selectedMonth, selectedYear]);

  const isLOPStatus = useCallback((status) => {
    if (!status || status === '-') return false;
    const u = String(status).toUpperCase();
    return u === 'LOP' || u === 'LWP' || u.includes('LOP') || u.includes('LWP');
  }, []);

  const handleChangeLOPDay = useCallback(async (day, newStatus) => {
    if (!employee) return;
    if (!isLocked) return; // self-service allowed only when month is locked (backend also enforces)
    const y = selectedYear;
    const m = selectedMonth + 1;
    const date = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSavingDay(day);
    try {
      const res = await attendanceService.updateMyLOPReversal(date, newStatus, `LOP Reversal (Employee)`);
      if (res?.success !== false) {
        setAttendanceData((prev) => ({ ...prev, [day]: res?.data?.status || newStatus }));
        setEditDay(null);
      }
    } catch (e) {
      console.error('Failed to update LOP reversal:', e);
    } finally {
      setSavingDay(null);
    }
  }, [employee, isLocked, selectedMonth, selectedYear]);

  // Calculate min and max dates for DatePicker
  const { minDate, maxDate, monthRangeValue } = useMemo(() => {
    const now = new Date();
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    let min = null;
    if (employee?.joiningDate) {
      const joinDate = new Date(employee.joiningDate);
      min = `${joinDate.getFullYear()}-${String(joinDate.getMonth() + 1).padStart(2, '0')}-01`;
    }
    
    const max = `${currentMonthEnd.getFullYear()}-${String(currentMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(currentMonthEnd.getDate()).padStart(2, '0')}`;
    
    // Format for DatePicker monthYearOnly: YYYY-MM-01/YYYY-MM-DD
    const currentMonthStart = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
    const currentMonthEndStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(new Date(selectedYear, selectedMonth + 1, 0).getDate()).padStart(2, '0')}`;
    const monthRange = `${currentMonthStart}/${currentMonthEndStr}`;
    
    return { minDate: min, maxDate: max, monthRangeValue: monthRange };
  }, [employee, selectedMonth, selectedYear]);

  // Handle month selection from DatePicker
  const handleMonthChange = useCallback((event) => {
    const value = event.target.value;
    if (!value) return;
    
    const [start] = value.split('/');
    if (!start) return;
    
    const [yearString, monthString] = start.split('-');
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1; // Convert to 0-based
    
    if (!Number.isNaN(parsedYear) && !Number.isNaN(parsedMonth)) {
      setSelectedYear(parsedYear);
      setSelectedMonth(Math.min(Math.max(parsedMonth, 0), 11));
    }
  }, []);

  // Calendar calculations
  const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();
  const totalDays = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const totalCells = 42; // 6 rows * 7 days
  const today = new Date();
  const todayNum = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

  // Check if a date is in the future (date-only comparison, no time)
  const isFutureDate = useCallback((day) => {
    const cellNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
    return cellNum > todayNum;
  }, [selectedYear, selectedMonth, todayNum]);

  // When locked: compute default PR/WO/PH for days with no backend data (same as admin)
  const effectiveAttendance = useMemo(() => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const result = { ...attendanceData };
    if (!employee) return result;

    const joinDate = employee.joiningDate || employee.joinDate;
    const relievedDate = employee.relievedDate || employee.exitDate || employee.relievingDate;
    let joinNum = 0;
    let relieveNum = 99999999;
    if (joinDate) {
      const d = new Date(joinDate);
      if (!isNaN(d.getTime())) joinNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    if (relievedDate) {
      const d = new Date(relievedDate);
      if (!isNaN(d.getTime())) relieveNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }

    const isPolicyEffectiveOnDate = (policy, date) => {
      if (!policy?.effectiveDate) return true;
      const ed = new Date(policy.effectiveDate);
      return !isNaN(ed.getTime()) && ed <= date;
    };

    const empId = employee.id || employee.employeeId || employee.empCode;
    const woMapping = employeeWeeklyOffMappings[empId] || employeeWeeklyOffMappings[String(empId)];
    const policyIds = woMapping ? (Array.isArray(woMapping) ? woMapping : woMapping.policies || (woMapping.policyId != null ? [woMapping.policyId] : [])) : [];

    const isWeeklyOff = (date) => {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      let policy = weeklyOffPolicies.find((p) => policyIds.includes(p.id));
      if (!policy) policy = weeklyOffPolicies.find((p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs?.[dayName]?.enabled);
      const wo = policy?.weeklyOffs?.[dayName];
      return !!(wo && wo.enabled);
    };

    const dateString = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isHoliday = (date) => {
      const ds = date.toISOString().split('T')[0];
      if ((holidaysData || []).some((h) => h.date === ds && h.isGlobal)) return true;
      const empPolicyIds = employeeHolidayMappings[empId] || employeeHolidayMappings[String(empId)] || [];
      const policies = (holidayPolicies || []).filter((p) => empPolicyIds.includes(p.id));
      return policies.some((p) => (p.holidays || []).some((h) => (h.date || h) === ds || (typeof h === 'object' && h.date === ds)));
    };

    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
      if (currentNum < joinNum || currentNum > relieveNum) continue;
      if (result[day] && result[day] !== '-') continue;
      const date = new Date(selectedYear, selectedMonth, day);
      if (isHoliday(date)) result[day] = 'PH';
      else if (isWeeklyOff(date)) result[day] = 'WO';
      else result[day] = 'PR';
    }
    return result;
  }, [employee, attendanceData, selectedMonth, selectedYear, weeklyOffPolicies, employeeWeeklyOffMappings, holidaysData, holidayPolicies, employeeHolidayMappings]);

  // Get status for a specific day (when locked uses effectiveAttendance so PR/WO/PH show like admin)
  const getDayStatus = useCallback((day) => {
    if (isFutureDate(day)) return null;
    return effectiveAttendance[day] || null;
  }, [effectiveAttendance, isFutureDate]);

  // Paid/unpaid leave codes for pay-days calculation (same logic as admin)
  const { unpaidLeaveCodes, paidLeaveCodes } = useMemo(() => {
    const unpaid = new Set(['LOP']);
    const paid = new Set(['PR', 'PH', 'WO']);
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || '').trim().toUpperCase();
      if (!code) return;
      if (lt.isPaid === false || lt.is_paid === false) unpaid.add(code);
      else paid.add(code);
    });
    return { unpaidLeaveCodes: unpaid, paidLeaveCodes: paid };
  }, [legendLeaveTypes]);

  // Known status codes for splitting combined statuses (e.g. PRCL -> PR + CL)
  const knownStatusCodes = useMemo(() => {
    const set = new Set(['PR', 'PH', 'WO', 'CL', 'EL', 'CO', 'CW', 'LOP']);
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || '').trim().toUpperCase();
      if (code) set.add(code);
    });
    return set;
  }, [legendLeaveTypes]);

  // Status counts for the selected month (uses effectiveAttendance so when locked PR/WO/PH are counted)
  const statusCounts = useMemo(() => {
    const counts = { PR: 0, PH: 0, WO: 0, CL: 0, EL: 0, CO: 0, CW: 0, LOP: 0 };
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    let joinDateNum = 0;
    let relievedDateNum = 99999999;
    if (employee?.joiningDate) {
      const d = new Date(employee.joiningDate);
      if (!isNaN(d.getTime())) joinDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    const relieved = employee?.relievedDate || employee?.exitDate || employee?.relievingDate;
    if (relieved) {
      const d = new Date(relieved);
      if (!isNaN(d.getTime())) relievedDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    const normalize = (s) => String(s || '').trim().toUpperCase();
    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
      if (currentNum < joinDateNum || currentNum > relievedDateNum) continue;
      let status = effectiveAttendance[day];
      if (!status || status === '-') continue;
      status = normalize(status);
      // Combined status (e.g. PRCL, LWPPR)
      const trySplit = (str) => {
        if (str.length <= 3 && knownStatusCodes.has(str)) return [str];
        for (let len = 3; len >= 2; len--) {
          const first = str.substring(0, len);
          const rest = str.substring(len);
          if (knownStatusCodes.has(first) && rest && knownStatusCodes.has(rest)) return [first, rest];
        }
        return [str];
      };
      const parts = trySplit(status);
      if (parts.length >= 2) {
        parts.forEach((p) => {
          const key = normalize(p);
          if (counts[key] === undefined) counts[key] = 0;
          counts[key] += 0.5;
        });
      } else {
        const key = parts[0] ? normalize(parts[0]) : status;
        if (counts[key] === undefined) counts[key] = 0;
        counts[key] += 1;
      }
    }
    return counts;
  }, [effectiveAttendance, selectedMonth, selectedYear, employee, knownStatusCodes]);

  // Pay days = sum of paid status counts (same logic as admin)
  const payDays = useMemo(() => {
    let total = 0;
    Object.entries(statusCounts || {}).forEach(([code, val]) => {
      const c = Number(val) || 0;
      if (c === 0) return;
      const upper = String(code).toUpperCase();
      if (unpaidLeaveCodes && unpaidLeaveCodes.has(upper)) return;
      if (upper === 'LOP') return;
      if (paidLeaveCodes && paidLeaveCodes.has(upper)) total += c;
      else total += c; // unknown code treat as paid
    });
    return total;
  }, [statusCounts, paidLeaveCodes, unpaidLeaveCodes]);

  // Totals object for the Totals bar (like admin "Calculate and Show Summary")
  const totals = useMemo(() => ({
    ...statusCounts,
    payDays,
  }), [statusCounts, payDays]);

  // Selectable codes only: from attendance config + leave types (same as admin dropdown). No hardcoding.
  const selectableCodesOrdered = useMemo(() => {
    const fromConfig = (legendConfigs || [])
      .filter((c) => c.customCode || c.originalCode || c.original_code)
      .map((c) => String(c.customCode || c.originalCode || c.original_code || '').toUpperCase())
      .filter(Boolean);
    const configSet = new Set(fromConfig);
    const fromLeave = (legendLeaveTypes || [])
      .filter((lt) => lt.code && (lt.isActive !== false && lt.is_active !== false))
      .map((lt) => String(lt.code || '').toUpperCase())
      .filter((code) => code && !configSet.has(code));
    return [...fromConfig, ...fromLeave];
  }, [legendConfigs, legendLeaveTypes]);

  // Full form per code: from config.meaning or leaveType.name only (no hardcoded map)
  const fullFormByCode = useMemo(() => {
    const map = { payDays: 'Pay Days' };
    (legendConfigs || []).forEach((c) => {
      const code = String(c.customCode || c.originalCode || c.original_code || '').toUpperCase();
      if (code && (c.meaning || c.meaning === 0)) map[code] = c.meaning;
    });
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || '').toUpperCase();
      if (code && (lt.name || lt.name === 0)) map[code] = lt.name;
    });
    return map;
  }, [legendConfigs, legendLeaveTypes]);

  const getFullForm = useCallback((code) => {
    if (code === 'payDays') return fullFormByCode.payDays || 'Pay Days';
    const upper = String(code || '').toUpperCase();
    return fullFormByCode[upper] ?? (upper || code);
  }, [fullFormByCode]);

  // Legend entries: only selectable codes + payDays, each with full form from API (config/leave types)
  const legendEntries = useMemo(() => {
    const keys = [...selectableCodesOrdered, 'payDays'];
    return keys.map((code) => {
      const label = getFullForm(code);
      const upper = code === 'payDays' ? code : String(code).toUpperCase();
      const style = code === 'payDays'
        ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
        : (statusStyles[upper] || 'bg-gray-100 text-gray-800 border-gray-200');
      const leaveType = legendLeaveTypes?.find((lt) => String(lt.code || '').toUpperCase() === upper);
      const color = leaveType?.color ? (leaveType.color.startsWith('#') ? leaveType.color : `#${leaveType.color}`) : null;
      return { code: upper === code ? code : upper, label, style, color, isPayDays: code === 'payDays' };
    });
  }, [selectableCodesOrdered, getFullForm, legendLeaveTypes]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-lg font-semibold">Attendance Calendar</h1>
          <div className="flex items-center gap-2">
            <DatePicker
              value={monthRangeValue}
              onChange={handleMonthChange}
              min={minDate}
              max={maxDate}
              monthYearOnly={true}
              showMonthName={true}
              dropdownAlign="auto"
              className="p-1 border rounded text-sm"
            />
          </div>
        </div>

        <div className="rounded-lg border bg-white overflow-hidden">
          {loadingAttendance && (
            <div className="px-4 py-3 border-b bg-gray-50 text-gray-700 flex items-center justify-center gap-2">
              <p className="text-sm font-medium">Loading attendance…</p>
            </div>
          )}

          <div className="grid grid-cols-7 border-b">
            {days.map((day) => (
              <div
                key={day}
                className="px-1 py-1 text-center text-base font-medium text-gray-500"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {Array.from({ length: totalCells }).map((_, index) => {
              const dayNumber = index - firstDayOfMonth + 1;
              const isCurrentMonth = dayNumber > 0 && dayNumber <= totalDays;
              const isFuture = isCurrentMonth && isFutureDate(dayNumber);

              const status = isCurrentMonth ? getDayStatus(dayNumber) : null;
              const displayStatus = status
                ? getDisplayStatus(status, attendanceMapping)
                : null;
              const canEditThisCell =
                isCurrentMonth &&
                !isFuture &&
                isLocked &&
                isLOPStatus(status) &&
                savingDay !== dayNumber;

              return (
                <div
                  key={index}
                  className={`min-h-[60px] border-b border-r p-1 ${
                    index % 7 === 6 ? 'border-r-0' : ''
                  } ${index >= 35 ? 'border-b-0' : ''} ${
                    isFuture ? 'bg-gray-50' : ''
                  }`}
                >
                  {isCurrentMonth && (
                    <div className="space-y-0.5">
                      <div
                        className={`text-xs font-medium ${
                          isFuture ? 'text-gray-400' : ''
                        }`}
                      >
                        {dayNumber}
                      </div>
                      {displayStatus && (
                        <>
                          {canEditThisCell && editDay === dayNumber ? (
                            <select
                              value={displayStatus}
                              onChange={(e) => handleChangeLOPDay(dayNumber, e.target.value)}
                              onBlur={() => setEditDay(null)}
                              autoFocus
                              className="mt-0.5 w-full rounded border px-1 py-0.5 text-[10px] font-medium"
                            >
                              {(selectableCodesOrdered || [])
                                .filter((c) => String(c).toUpperCase() !== 'LOP' && String(c).toUpperCase() !== 'LWP')
                                .map((code) => (
                                  <option key={code} value={code}>
                                    {code} – {getFullForm(code)}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <div
                              role={canEditThisCell ? 'button' : undefined}
                              tabIndex={canEditThisCell ? 0 : undefined}
                              onClick={() => {
                                if (!canEditThisCell) return;
                                setEditDay(dayNumber);
                              }}
                              className={`mt-0.5 inline-flex items-center rounded border px-1 py-0.5 text-[10px] font-medium ${getStatusStyle(
                                status,
                                attendanceMapping
                              )} ${canEditThisCell ? 'cursor-pointer hover:opacity-90' : ''}`}
                              title={canEditThisCell ? 'Click to change this LOP day' : ''}
                            >
                              {savingDay === dayNumber ? 'Saving…' : displayStatus}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals bar only when locked: only selectable types (config + leave types), same order as Legend */}
        {isLocked && (
          <div className="mt-4 rounded-lg border bg-gray-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-gray-700">Totals:</span>
              {selectableCodesOrdered.map((code) => {
                const upper = String(code).toUpperCase();
                const leaveType = legendLeaveTypes?.find((lt) => String(lt.code || '').toUpperCase() === upper);
                const color = leaveType?.color ? (leaveType.color.startsWith('#') ? leaveType.color : `#${leaveType.color}`) : null;
                const style = statusStyles[upper] || 'bg-gray-100 text-gray-800 border-gray-200';
                if (color) {
                  return (
                    <span
                      key={code}
                      className="rounded px-2 py-1"
                      style={{ backgroundColor: `${color}20`, color, borderColor: color }}
                    >
                      {code}: {totals[upper] ?? 0}
                    </span>
                  );
                }
                return (
                  <span key={code} className={`rounded px-2 py-1 ${style}`}>
                    {code}: {totals[upper] ?? 0}
                  </span>
                );
              })}
              <span className="rounded bg-indigo-100 px-2 py-1 font-medium text-indigo-800">Pay Days: {totals.payDays ?? 0}</span>
            </div>
          </div>
        )}

        {isLocked && (
          <div className="mt-1 flex flex-wrap gap-1">
            <div className="text-[15px] align-middle text-gray-500">
              <b>Legend:</b>
            </div>
            {legendEntries.map((entry) => {
              const displayCode = entry.isPayDays ? entry.code : (attendanceMapping?.mapping?.[entry.code] || entry.code);
              const fullForm = entry.label;
              const text = entry.isPayDays ? fullForm : `${displayCode} – ${fullForm}`;
              if (entry.color) {
                return (
                  <div
                    key={`legend-${entry.code}`}
                    className="inline-flex items-center rounded border px-1 py-0.5 text-[10px] font-medium"
                    style={{
                      backgroundColor: `${entry.color}20`,
                      color: entry.color,
                      borderColor: entry.color,
                    }}
                  >
                    {text}
                  </div>
                );
              }
              return (
                <div
                  key={`legend-${entry.code}`}
                  className={`inline-flex items-center rounded border px-1 py-0.5 text-[10px] font-medium ${entry.style}`}
                >
                  {text}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
